import {Box, Switch, Text} from '@mantine/core';
import React from 'react';

import {FILTER_PREDICATES_BY_MEASUREMENT_TYPE, MeasurementType} from 'samples/MeasurementForm';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';
import {NumericInputChangeEvent} from 'util/Types';

import {
  ConcentrationSettings as ConcentrationSettingsType,
  ConcentrationSettingsWithDilutionSettings,
  DilutionSettings as DilutionSettingsType,
} from '../concentration-layers/machine/ConcentrationLayerMachine';
import DilutionSettings from './DilutionSettings';

export interface ConcentrationSettingsProps {
  /** The concentration settings to configure. */
  concentrationSettings: ConcentrationSettingsType;
  dilutionSettings: DilutionSettingsType;
  /** Callback function to update concentration settings */
  onUpdateConcentrationSettings: (settings: Partial<ConcentrationSettingsType>) => void;
  /** Callback function to update dilution settings */
  onUpdateLayerDilutionSettings: (settings: Partial<DilutionSettingsType>) => void;
}

/**
 * Determines if the current concentration settings are valid.
 *
 * The settings are invalid if any of the following are true:
 * - The concentration is not set
 * - The concentration unit is not set
 * - Dilution settings are enabled but:
 *   - The dilution factor is not set or is less than or equal to 0
 *   - The replicates are not set or are less than 1 or are not an integer
 *
 * @param settings - The concentration settings to check.
 * @returns True if the settings are valid, false otherwise.
 */
export function isConcentrationSettingsValid(settings: ConcentrationSettingsWithDilutionSettings): boolean {
  const {concentration, concentrationUnit, allowSetDilutionSettings, dilutionSettings} = settings;
  if (concentration == null || concentrationUnit == null) {
    return false;
  }
  if (allowSetDilutionSettings) {
    const {dilutionFactor, replicates} = dilutionSettings;
    if (dilutionFactor == null || dilutionFactor <= 0) {
      return false;
    }
    if (replicates == null || replicates < 1 || !Number.isInteger(replicates)) {
      return false;
    }
  }
  return true;
}

/**
 * Component for configuring concentration settings including units, concentration value,
 * and dilution settings toggle.
 */
export default function ConcentrationSettings({
  concentrationSettings,
  dilutionSettings,
  onUpdateConcentrationSettings,
  onUpdateLayerDilutionSettings,
}: ConcentrationSettingsProps): JSX.Element {
  return (
    <>
      <Box>
        <FieldLabel label="Units" isRequired={true} />
        <SearchableUnitDropdown
          selectedId={concentrationSettings.concentrationUnit?.id}
          unitTypeFilter={FILTER_PREDICATES_BY_MEASUREMENT_TYPE[MeasurementType.CONCENTRATION]}
          displaySelectionAsSymbol={true}
          onSelect={(unit) => {
            onUpdateConcentrationSettings({concentrationUnit: unit});
          }}
        />
        <Text size="sm" c="dimmed">
          Each layer can only have one unit.
        </Text>
      </Box>
      <Box>
        <FieldLabel label="Concentration" isRequired={true} />
        <NumericInput
          value={concentrationSettings.concentration ?? ''}
          min={0}
          onChange={(e: NumericInputChangeEvent) => {
            onUpdateConcentrationSettings({concentration: e.target.numericValue});
          }}
          isDisabled={false}
          required={true}
          validateInteger={false}
        />
      </Box>
      <Switch
        label="Set dilution"
        styles={{label: {fontWeight: 'bold'}}}
        checked={concentrationSettings.allowSetDilutionSettings}
        onChange={() => {
          onUpdateConcentrationSettings({
            allowSetDilutionSettings: !concentrationSettings.allowSetDilutionSettings,
          });
        }}
      />
      {concentrationSettings.allowSetDilutionSettings && (
        <DilutionSettings
          dilutionSettings={dilutionSettings}
          onUpdateLayerDilutionSettings={(settings: Partial<DilutionSettingsType>) => {
            onUpdateLayerDilutionSettings(settings);
          }}
        />
      )}
    </>
  );
}
